import { useCallback } from "react";
import { useHttpClient } from "shared/hooks/http-hook";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/customer-portal`;

const useCustomerPortalApi = () => {
  const { isLoading, error, sendRequest, clearError } = useHttpClient();

  const openCount = useCallback(async () => {
    const data = await sendRequest(`${BASE}/requests/open-count`);
    return data?.count || 0;
  }, [sendRequest]);

  const listSchools = useCallback(async () => {
    const data = await sendRequest(`${BASE}/schools`);
    return data?.schools || [];
  }, [sendRequest]);

  const listRequests = useCallback(
    async ({ status, school, open } = {}) => {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (school) params.set("school", school);
      if (open) params.set("open", "1");
      const qs = params.toString();
      const data = await sendRequest(`${BASE}/requests${qs ? `?${qs}` : ""}`);
      return data?.requests || [];
    },
    [sendRequest],
  );

  const getRequest = useCallback(
    async (id) => {
      if (!id) return null;
      const data = await sendRequest(`${BASE}/requests/${id}`);
      return data?.request || null;
    },
    [sendRequest],
  );

  const createRequest = useCallback(
    async (fields, files = []) => {
      const formData = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        formData.append(key, value);
      });
      (files || []).forEach((file) => formData.append("files", file));
      const data = await sendRequest(`${BASE}/requests`, "POST", formData, {}, false);
      return data?.request || null;
    },
    [sendRequest],
  );

  const addComment = useCallback(
    async (id, body, visibility) => {
      const data = await sendRequest(
        `${BASE}/requests/${id}/comments`,
        "POST",
        JSON.stringify({ body, visibility, language: localStorage.getItem("i18nextLng") || "tr" }),
      );
      return data?.request || null;
    },
    [sendRequest],
  );

  const addAttachments = useCallback(
    async (id, files = []) => {
      const formData = new FormData();
      formData.append("language", localStorage.getItem("i18nextLng") || "tr");
      (files || []).forEach((file) => formData.append("files", file));
      const data = await sendRequest(`${BASE}/requests/${id}/attachments`, "POST", formData, {}, false);
      return data?.request || null;
    },
    [sendRequest],
  );

  const deleteAttachment = useCallback(
    async (id, attachmentId) => {
      const data = await sendRequest(
        `${BASE}/requests/${id}/attachments/${attachmentId}`,
        "DELETE",
        JSON.stringify({ language: localStorage.getItem("i18nextLng") || "tr" }),
      );
      return data?.request || null;
    },
    [sendRequest],
  );

  const deleteRequest = useCallback(
    async (id) => {
      const data = await sendRequest(
        `${BASE}/requests/${id}`,
        "DELETE",
        JSON.stringify({ language: localStorage.getItem("i18nextLng") || "tr" }),
      );
      return data;
    },
    [sendRequest],
  );

  const patchStatus = useCallback(
    async (id, status) => {
      const data = await sendRequest(
        `${BASE}/requests/${id}`,
        "PATCH",
        JSON.stringify({ status, language: localStorage.getItem("i18nextLng") || "tr" }),
      );
      return data?.request || null;
    },
    [sendRequest],
  );

  const searchStudents = useCallback(
    async (q, school) => {
      const params = new URLSearchParams();
      params.set("q", q || "");
      if (school) params.set("school", school);
      const data = await sendRequest(`${BASE}/students/search?${params.toString()}`);
      return data?.students || [];
    },
    [sendRequest],
  );

  const listServiceNames = useCallback(
    async (school) => {
      const qs = school ? `?school=${encodeURIComponent(school)}` : "";
      const data = await sendRequest(`${BASE}/services/names${qs}`);
      return data?.services || [];
    },
    [sendRequest],
  );

  const listContacts = useCallback(async () => {
    const data = await sendRequest(`${BASE}/contacts`);
    return data?.clients || [];
  }, [sendRequest]);

  const inviteContact = useCallback(
    async (payload) => {
      return sendRequest(`${BASE}/invite`, "POST", JSON.stringify(payload));
    },
    [sendRequest],
  );

  const removeContact = useCallback(
    async (id) => {
      return sendRequest(`${BASE}/contacts/${id}`, "DELETE");
    },
    [sendRequest],
  );

  const downloadAttachment = useCallback(
    async (requestId, attachmentId, fileName) => {
      const blob = await sendRequest(
        `${BASE}/requests/${requestId}/attachments/${attachmentId}`,
        "GET",
        null,
        {},
        false,
        true,
      );
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName || "attachment";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    [sendRequest],
  );

  return {
    isLoading,
    error,
    clearError,
    listSchools,
    listRequests,
    getRequest,
    createRequest,
    addComment,
    addAttachments,
    deleteAttachment,
    deleteRequest,
    patchStatus,
    searchStudents,
    listServiceNames,
    listContacts,
    inviteContact,
    removeContact,
    downloadAttachment,
    openCount,
  };
};

export default useCustomerPortalApi;
